import React, { useState, useEffect } from "react";
import { FiCheck, FiInfo } from "react-icons/fi";
import * as XLSX from "xlsx";
import styles from "./StaticPricingCards.module.css";

const CURRENCY_SUFFIX = { INR: "inr", USD: "usd", EUR: "euro" };
const CURRENCY_SYMBOL = { INR: "₹", USD: "$", EUR: "€" };

const PLANS = [
  {
    key: "starter",
    title: "Starter",
    desc: "Best for startups and businesses beginning their communication journey.",
    promo: "Get the first 30 days free",
    sectionLabel: "What's included",
    features: [
      "WhatsApp, SMS & RCS",
      "Pay-as-you-use communication",
      "Free WhatsApp Chat Widget",
      "Human Agent as add-on",
      "AI Chat Agent as add-on",
      "Access to AI services",
    ],
    footerBold: "Platform Fee Waiver:",
    footerText: " ₹1,000 platform fee waived when monthly usage billing crosses ₹10,000.",
    popular: false,
  },
  {
    key: "pro",
    title: "Pro",
    desc: "Best for growing teams managing customer conversations.",
    promo: null,
    sectionLabel: "Everything in Starter, plus",
    features: [
      "3 Human Agent seats",
      "Free WhatsApp Chat Widget",
      "WhatsApp, SMS & RCS",
      "Pay-as-you-use communication",
      "AI Chat Agent available as add-on",
      "AI services available as add-ons",
    ],
    footerBold: "Communication Usage:",
    footerText: " WhatsApp, SMS and RCS usage is charged separately.",
    popular: false,
  },
  {
    key: "business",
    title: "Business",
    desc: "Best for businesses combining human and AI-powered customer engagement.",
    promo: null,
    sectionLabel: "Everything in Pro, plus",
    features: [
      "5 Human Agent seats",
      "1 AI Chat Agent included",
      "5 million AI tokens/month",
      "White-labelled WhatsApp Chat Widget",
      "Access to all supported channels",
      "AI-powered customer engagement",
    ],
    footerBold: "Included AI Usage:",
    footerText: " 5 million AI tokens included every month. Additional tokens can be purchased separately.",
    popular: true,
  },
  {
    key: "scale",
    title: "Scale",
    desc: "Best for larger customer-support, sales and engagement teams.",
    promo: null,
    sectionLabel: "Everything in Business, plus",
    features: [
      "7 Human Agent seats",
      "1 AI Chat Agent included",
      "5 million AI tokens/month",
      "White-labelled WhatsApp Chat Widget",
      "Complete widget analytics",
      "Advanced customer engagement capabilities",
    ],
    footerBold: "Built for Scale:",
    footerText: " Advanced tools for larger customer-support, sales and engagement teams.",
    popular: false,
  },
];

const FEATURE_TOOLTIPS = {
  pro: {
    "Pay-as-you-use communication": "Extra credit needs to be added for sending WhatsApp messages.",
  },
};

const StaticPricingCards = ({ countryCurrency = "INR", onBuyPlan, userActivePlans = [] }) => {
  const [priceRow, setPriceRow] = useState(null);
  const [openTooltip, setOpenTooltip] = useState(null);

  const suffix = CURRENCY_SUFFIX[countryCurrency] || "inr";
  const symbol = CURRENCY_SYMBOL[countryCurrency] || "₹";

  useEffect(() => {
    fetch("/pricing.xlsx")
      .then((res) => res.arrayBuffer())
      .then((buffer) => {
        const wb = XLSX.read(buffer, { type: "array" });
        const ws = wb.Sheets["plans&pricing"] || wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws);
        if (rows.length > 0) setPriceRow(rows[0]);
      })
      .catch((err) => console.error("Failed to load pricing.xlsx:", err));
  }, []);

  useEffect(() => {
    if (!openTooltip) return;
    const handleOutsideClick = (e) => {
      if (!e.target.closest("[data-info-wrap]")) setOpenTooltip(null);
    };
    document.addEventListener("click", handleOutsideClick);
    return () => document.removeEventListener("click", handleOutsideClick);
  }, [openTooltip]);

  const hasAnyActiveStaticPlan = PLANS.some((plan) => {
    const matchedPlan = userActivePlans.find((p) => {
      if (p.plan_title === plan.title) return true;
      if (p.add_on && typeof p.add_on === "object") {
        return Object.keys(p.add_on).includes(plan.title);
      }
      return false;
    });
    return !!matchedPlan && !matchedPlan.is_expired;
  });

  return (
    <div className={styles.wrapper}>

      <div className={styles.topHeader}>
        <h4 className={styles.topHeading}>Flexible Plans Built for Every Business</h4>
        <p className={styles.topSub}>Start using our service today with a minimum payment and enjoy 30 days with zero platform fees.</p>
      </div>

      <div className={styles.grid}>
        {PLANS.map((plan) => {
          const rawPrice = priceRow ? priceRow[`${plan.key}_${suffix}`] : null;
          const displayPrice = rawPrice != null
            ? `${symbol}${Number(rawPrice).toLocaleString()}`
            : "—";

          const matchedPlan = userActivePlans.find((p) => {
            if (p.plan_title === plan.title) return true;
            if (p.add_on && typeof p.add_on === "object") {
              return Object.keys(p.add_on).includes(plan.title);
            }
            return false;
          });
          const isExpired = !!matchedPlan && !!matchedPlan.is_expired;
          const isDefaultStarter = plan.key === "starter" && !hasAnyActiveStaticPlan;
          const isActive = (!!matchedPlan && !isExpired) || isDefaultStarter;

          return (
            <div
              key={plan.key}
              className={`${styles.card} ${plan.popular ? styles.popular : ""}`}
            >
              {plan.popular && (
                <div className={styles.popularBadge}>MOST POPULAR</div>
              )}

              <div className={styles.cardBody}>
                <h3 className={styles.planTitle}>{plan.title}</h3>
                <p className={styles.planDesc}>{plan.desc}</p>

                <div className={styles.priceRow}>
                  <span className={styles.price}>{displayPrice}</span>
                  <span className={styles.priceSub}>/month</span>
                </div>
                {plan.promo && <p className={styles.promo}>{plan.promo}</p>}

                <div className={styles.divider} />

                <p className={styles.sectionLabel}>{plan.sectionLabel}</p>
                <ul className={styles.features}>
                  {plan.features.map((f) => {
                    const tooltipText = FEATURE_TOOLTIPS[plan.key]?.[f];
                    const tooltipKey = `${plan.key}-${f}`;
                    return (
                      <li key={f} className={styles.feature}>
                        <FiCheck className={styles.checkIcon} />
                        <span>{f}</span>
                        {tooltipText && (
                          <span className={styles.infoWrap} data-info-wrap>
                            <button
                              type="button"
                              className={styles.infoBtn}
                              aria-label="More info"
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenTooltip((prev) => (prev === tooltipKey ? null : tooltipKey));
                              }}
                            >
                              <FiInfo size={12} />
                            </button>
                            {openTooltip === tooltipKey && (
                              <div className={styles.infoTooltip}>{tooltipText}</div>
                            )}
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>

              <div className={styles.cardFooter}>
                <div className={styles.footerNote}>
                  <strong>{plan.footerBold}</strong>
                  {plan.footerText}
                </div>
                {isActive ? (
                  <span className={styles.activeBadge}><FiCheck size={13} /> Active</span>
                ) : isExpired ? (
                  <div className={styles.footerActions}>
                    <span className={styles.expiredBadge}>Expired</span>
                    <button
                      type="button"
                      className={`${styles.btn} ${styles.btnRenew}`}
                      onClick={() => onBuyPlan && onBuyPlan(plan.title)}
                    >
                      Renew Plan
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    className={`${styles.btn} ${plan.popular ? styles.btnPrimary : styles.btnOutline}`}
                    onClick={() => onBuyPlan && onBuyPlan(plan.title)}
                  >
                    Get Started
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <p className={styles.bottomNote}>
        <strong>Note:</strong> Platform fees cover platform access and included features. Communication usage is charged separately based on actual usage. Applicable taxes are charged additionally.
      </p>
    </div>
  );
};

export default StaticPricingCards;
